import type { SaasActionRequiredType } from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import { AuthTypes, UserStatus } from 'src/enums'

export type ResponseErrorData = {
  error?: string
  exceptionType?: SaasActionRequiredType
} & Record<string, unknown>

export type ResponseError = {
  response?: {
    status?: number
    data: ResponseErrorData
  }
  config?: unknown
}

export type ResponseErrorHandlerDeps = {
  authType: string | undefined
  isAuthenticated: boolean
  isCspAuthenticated: boolean
  onAuthenticatedAuth0Unauthorized: () => void
  onCspAuthenticatedAuth0Unauthorized: () => void
  cognitoSessionValid: () => void
  downgradeActionRequiredType: SaasActionRequiredType
  userHasOrgAdminPermissions: boolean
  showDowngradedToStarterModal: (data: ResponseErrorData) => void
  showSaasActionRequiredModal: (data: ResponseErrorData) => void
}

export const makeResponseErrorHandler =
  (deps: ResponseErrorHandlerDeps) =>
  (error: ResponseError): Promise<never> => {
    const response = error.response

    // FORBIDDEN / NOT AUTHENTICATED
    if (response?.status === 401) {
      // A PHONE_NOT_VERIFIED 401 means the token was invalidated because the
      // user's phone number changed and needs re-verification (not because the
      // session expired). Skip logout so verification can finish.
      if (response.data?.error === UserStatus.PhoneNotVerified) {
        return Promise.reject(error)
      }

      if (deps.authType === AuthTypes.auth0) {
        if (deps.isAuthenticated) {
          deps.onAuthenticatedAuth0Unauthorized()
        }
        if (deps.isCspAuthenticated) {
          deps.onCspAuthenticatedAuth0Unauthorized()
        }
      } else if (deps.isAuthenticated || deps.isCspAuthenticated) {
        // EX-40256: a 401 before the session is established is not evidence it
        // expired, and signing out here strands sign-in in a loop.
        deps.cognitoSessionValid()
      }
      // UPGRADE/PAYMENT REQUIRED
    } else if (response?.status === 402) {
      if (
        response.data.exceptionType === deps.downgradeActionRequiredType &&
        deps.userHasOrgAdminPermissions
      ) {
        deps.showDowngradedToStarterModal(response.data)
      } else {
        deps.showSaasActionRequiredModal(response.data)
      }
    }

    return Promise.reject(error)
  }
